import type { RealtimeConditionScope } from "src/components/explore/context/hooks";

export type RtaMembershipReferenceDisabledReason = "snapshotting_disabled";

export const RTA_MEMBERSHIP_SNAPSHOTTING_DISABLED_MESSAGE =
  "Enable audience snapshots on this source to reference realtime audiences";

/**
 * Snapshotting-disabled copy for the RTA "Show Members" button, where the user
 * is trying to browse members rather than reference the audience in a filter.
 */
export const RTA_MEMBERSHIP_SNAPSHOTTING_DISABLED_VIEW_MEMBERS_MESSAGE =
  "Enable audience snapshots on the source to view members";

/**
 * Path to a source's settings page, where audience snapshotting (the writeback
 * that materializes RTA membership) is enabled. Shared by every realtime-audience
 * surface that needs to point the user at the source to turn snapshotting on.
 */
export const getSourceSettingsPath = (connectionId: string | number): string =>
  `/sources/${connectionId}`;

export type RtaMembershipReferenceDisabledArgs = {
  isRealtimeAudience: boolean;
  parentSnapshottingEnabled: boolean;
};

export const getRtaMembershipReferenceDisabledReason = (
  args: RtaMembershipReferenceDisabledArgs,
): RtaMembershipReferenceDisabledReason | undefined => {
  const { isRealtimeAudience, parentSnapshottingEnabled } = args;

  if (!isRealtimeAudience) {
    return undefined;
  }

  if (!parentSnapshottingEnabled) {
    return "snapshotting_disabled";
  }

  return undefined;
};

export function getRtaMembershipReferenceDisabledMessage(
  reason: RtaMembershipReferenceDisabledReason | undefined,
): string | undefined;
export function getRtaMembershipReferenceDisabledMessage(
  args: RtaMembershipReferenceDisabledArgs,
): string | undefined;
export function getRtaMembershipReferenceDisabledMessage(
  reasonOrArgs:
    | RtaMembershipReferenceDisabledReason
    | undefined
    | RtaMembershipReferenceDisabledArgs,
): string | undefined {
  const reason =
    typeof reasonOrArgs === "object" && reasonOrArgs !== null
      ? getRtaMembershipReferenceDisabledReason(reasonOrArgs)
      : reasonOrArgs;

  switch (reason) {
    case "snapshotting_disabled":
      return RTA_MEMBERSHIP_SNAPSHOTTING_DISABLED_MESSAGE;
    default:
      return undefined;
  }
}

type AudienceWithRealtimeFlag = {
  is_realtime_audience: boolean;
};

const filterAudiencesByRealtimeFlag = <T extends AudienceWithRealtimeFlag>(
  audiences: readonly T[],
  includeRealtimeAudiences: boolean,
): T[] => {
  if (includeRealtimeAudiences) {
    return [...audiences];
  }

  return audiences.filter((audience) => !audience.is_realtime_audience);
};

type RelatedAudienceForSegmentSet = AudienceWithRealtimeFlag & {
  id: string | number;
};

/**
 * Filters sibling audiences for the SegmentSet picker.
 * Batch editors may list RTAs; RTA editors never may.
 * A realtime journey editor lists RTAs only.
 */
export const filterAudiencesForSegmentSetPicker = <
  T extends RelatedAudienceForSegmentSet,
>(
  audiences: readonly T[],
  args: {
    audienceId: string | number | null | undefined;
    realtimeConditionScope: RealtimeConditionScope | undefined;
  },
): T[] => {
  const { audienceId, realtimeConditionScope } = args;

  const withoutSelf = audiences.filter(
    (audience) => audience.id !== audienceId,
  );

  if (realtimeConditionScope === "journey-criteria") {
    return withoutSelf.filter((audience) => audience.is_realtime_audience);
  }

  return filterAudiencesByRealtimeFlag(
    withoutSelf,
    realtimeConditionScope !== "audience",
  );
};

export type JourneyEntryAudienceOption = RelatedAudienceForSegmentSet & {
  name: string;
  description?: string | null;
  connection?: {
    id?: string | number;
    audience_snapshotting_enabled?: boolean;
    definition?: { icon?: string };
  } | null;
};

export const filterAudiencesForJourneyEntryPicker = <
  T extends JourneyEntryAudienceOption,
>(
  audiences: readonly T[],
  args: {
    isRealtimeJourney: boolean;
  },
): T[] => {
  if (args.isRealtimeJourney) {
    return audiences.filter((audience) => audience.is_realtime_audience);
  }
  return [...audiences];
};
